import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { cn } from "@/cn";
import { listPlugins, togglePlugin, type PluginConfig } from "@/api/plugins";
import { useClientPagination } from "@/hooks/usePagination";
import { PaginationBar } from "@/components/pagination-bar";
import {
  STATUS_FILTER_TABS,
  countEnabledPlugins,
  filterPlugins,
  type PluginStatusFilter,
} from "@/lib/plugin-ui";

export function SettingsPluginsPage() {
  const qc = useQueryClient();
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<PluginStatusFilter>("all");

  const { data: plugins = [], isLoading, error } = useQuery<PluginConfig[]>({
    queryKey: ["plugins"],
    queryFn: listPlugins,
    staleTime: 30_000,
  });

  const filteredPlugins = filterPlugins(plugins, {
    search: searchQuery,
    status: statusFilter,
  });

  const enabledCount = countEnabledPlugins(plugins);

  const {
    pageItems,
    page,
    setPage,
    pageSize,
    setPageSize,
    totalPages,
    showingFrom,
    showingTo,
  } = useClientPagination(filteredPlugins, [searchQuery, statusFilter]);

  const toggleMutation = useMutation({
    mutationFn: togglePlugin,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["plugins"] }),
  });

  return (
    <div className="space-y-6">
      <header className="mb-8">
        <h1 className="text-2xl font-bold text-foreground">Plugins</h1>
        <p className="text-muted-foreground">
          App-wide plugin features. Toggle plugins on or off for all users.
        </p>
      </header>

      {!isLoading && !error && plugins.length > 0 && (
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-1 flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative flex-1">
              <svg
                className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
                />
              </svg>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name, description, or ID…"
                className="w-full rounded-md border border-border bg-card py-2 pl-10 pr-4 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand"
              />
            </div>
          </div>
          <span className="shrink-0 text-xs text-muted-foreground">
            {filteredPlugins.length === 0
              ? `0 of ${plugins.length} (${enabledCount} enabled)`
              : `Showing ${showingFrom}–${showingTo} of ${filteredPlugins.length} (${enabledCount} enabled)`}
          </span>
        </div>
      )}

      {!isLoading && !error && plugins.length > 0 && (
        <div className="flex gap-1">
          {STATUS_FILTER_TABS.map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setStatusFilter(tab)}
              className={cn(
                "rounded-md px-3 py-1.5 text-xs font-medium transition",
                statusFilter === tab
                  ? "bg-muted text-foreground"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {tab === "all" ? "All status" : tab === "enabled" ? "Enabled" : "Disabled"}
            </button>
          ))}
        </div>
      )}

      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-brand" />
        </div>
      )}
      {error && <p className="text-sm text-destructive">Failed to load plugins.</p>}

      {!isLoading && !error && plugins.length === 0 && (
        <div className="rounded-lg border border-dashed border-border p-8 text-center">
          <p className="text-sm text-muted-foreground">No plugins configured.</p>
        </div>
      )}

      {!isLoading && !error && plugins.length > 0 && filteredPlugins.length === 0 && (
        <div className="rounded-lg border border-border bg-card p-8 text-center">
          <p className="text-sm text-muted-foreground">No plugins match your filters.</p>
        </div>
      )}

      {!isLoading && !error && filteredPlugins.length > 0 && (
        <>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {pageItems.map((plugin) => (
              <div
                key={plugin.pluginId}
                className={cn(
                  "flex flex-col rounded-lg border bg-card p-4 transition",
                  plugin.enabled ? "border-border" : "border-border/50 bg-card/50 opacity-60",
                )}
              >
                <div className="mb-2 flex items-start justify-between gap-2">
                  <Link
                    to={`/settings/plugins/${plugin.pluginId}`}
                    className="min-w-0 flex-1"
                  >
                    <p className="text-sm font-medium text-foreground hover:text-brand">
                      {plugin.name}
                    </p>
                  </Link>
                  <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                    v{plugin.version}
                  </span>
                </div>
                <Link
                  to={`/settings/plugins/${plugin.pluginId}`}
                  className="mb-4 min-h-[2.5rem] flex-1"
                >
                  <p className="line-clamp-2 text-xs text-muted-foreground">
                    {plugin.description || plugin.pluginId}
                  </p>
                </Link>
                <div className="flex items-center justify-between border-t border-border pt-3">
                  <label
                    className="relative inline-flex cursor-pointer items-center"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={plugin.enabled}
                      onChange={() => toggleMutation.mutate(plugin.pluginId)}
                      className="peer sr-only"
                    />
                    <div className="h-5 w-9 rounded-full border border-border bg-muted after:absolute after:left-[2px] after:top-[2px] after:h-4 after:w-4 after:rounded-full after:bg-foreground after:transition-all peer-checked:bg-brand peer-checked:after:translate-x-full peer-checked:after:bg-brand-foreground" />
                    <span className="ml-2 text-xs text-muted-foreground">
                      {plugin.enabled ? "Enabled" : "Disabled"}
                    </span>
                  </label>
                  <Link
                    to={`/settings/plugins/${plugin.pluginId}`}
                    className="rounded-md border border-border px-2.5 py-1 text-xs text-muted-foreground transition hover:text-foreground"
                  >
                    Details
                  </Link>
                </div>
              </div>
            ))}
          </div>
          <PaginationBar
            page={page}
            pageSize={pageSize}
            totalItems={filteredPlugins.length}
            totalPages={totalPages}
            showingFrom={showingFrom}
            showingTo={showingTo}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        </>
      )}
    </div>
  );
}
